import { Box, Tooltip } from "@mui/material";
import React from "react";

interface ConfidenceIndicatorProps {
  confidence: number;
  width?: number;
  height?: number;
  colorScheme?: "color" | "gray";
}

const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({
  confidence,
  width = 60,
  height = 12,
  colorScheme = "color",
}) => {
  // Normalize confidence to 0-1 range and clamp values
  const normalizedConfidence = Math.max(0, Math.min(1, confidence));

  // Determine color based on confidence level
  const getColor = (conf: number, colorScheme: string): string => {
    if (colorScheme == "gray") {
      return "rgba(223, 223, 223, 1)";
    } else {
      if (conf <= 0.1) return "#f44336"; // Red
      if (conf <= 0.5) return "#ff9800"; // Orange/Yellow
      return "#4caf50"; // Green
    }
  };

  const getLabel = (conf: number): string => {
    if (conf <= 0.1) return "Low Confidence";
    if (conf <= 0.5) return "Medium Confidence";
    return "High Confidence";
  };

  const color = getColor(normalizedConfidence, colorScheme);
  const fillWidth = normalizedConfidence * width;

  return (
    <Tooltip title={`${getLabel(normalizedConfidence)} (${confidence})`}>
      <Box
        sx={{
          width: width,
          height: height,
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          borderRadius: height / 2,
          position: "relative",
          overflow: "hidden",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          cursor: "help",
        }}
      >
        <Box
          sx={{
            width: fillWidth,
            height: "100%",
            backgroundColor: color,
            borderRadius: height / 2,
            transition: "width 0.3s ease-in-out",
            minWidth: fillWidth > 0 ? 2 : 0, // Ensure visibility even for very low values
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default ConfidenceIndicator;
